import type { Routes } from '@angular/router';

import { authMatchGuard, loginRedirectGuard } from '@ebt/shared-auth';

/**
 * Customer portal routes. Same gating pattern as the staff CRM:
 * `loginRedirectGuard` bounces authenticated users off `/login`, and
 * `authMatchGuard` guards the rest. The authenticated subtree mounts
 * the portal app shell as a parent so the sidebar + topbar persist
 * across feature routes (jobs, quotes, invoices, messages, profile).
 */
export const routes: Routes = [
  {
    path: 'login',
    title: 'Sign in · EBT Portal',
    canMatch: [loginRedirectGuard],
    loadComponent: () => import('./auth/login.page').then((m) => m.LoginPage),
  },
  {
    // Public — reachable while logged out. Linked from the login page.
    path: 'forgot-password',
    title: 'Forgot password · EBT Portal',
    loadComponent: () =>
      import('./auth/forgot-password.page').then((m) => m.ForgotPasswordPage),
  },
  {
    // Public — the target of the reset email:
    // `${portalBaseUrl}/portal/reset-password?token=…`.
    path: 'portal/reset-password',
    title: 'Reset password · EBT Portal',
    loadComponent: () =>
      import('./auth/reset-password.page').then((m) => m.ResetPasswordPage),
  },
  {
    // Public — the target of the invitation email:
    // `${portalBaseUrl}/portal/accept-invitation?token=…`.
    path: 'portal/accept-invitation',
    title: 'Accept invitation · EBT Portal',
    loadComponent: () =>
      import('./auth/accept-invitation.page').then((m) => m.AcceptInvitationPage),
  },
  {
    path: '',
    canMatch: [authMatchGuard],
    loadComponent: () => import('./shell/app-shell.component').then((m) => m.AppShellComponent),
    children: [
      {
        path: '',
        pathMatch: 'full',
        title: 'Dashboard · EBT Portal',
        loadComponent: () => import('./dashboard/dashboard.page').then((m) => m.DashboardPage),
      },
      {
        path: 'quotes',
        title: 'My Quotes · EBT Portal',
        loadComponent: () => import('./quotes/quotes-list.page').then((m) => m.QuotesListPage),
      },
      {
        path: 'quotes/:id',
        title: 'Quote · EBT Portal',
        loadComponent: () => import('./quotes/quote-detail.page').then((m) => m.QuoteDetailPage),
      },
      {
        path: 'jobs',
        title: 'My Jobs · EBT Portal',
        loadComponent: () => import('./jobs/jobs-list.page').then((m) => m.JobsListPage),
      },
      {
        path: 'jobs/:id',
        title: 'Job · EBT Portal',
        loadComponent: () => import('./jobs/job-detail.page').then((m) => m.JobDetailPage),
      },
      {
        path: 'invoices',
        title: 'My Invoices · EBT Portal',
        loadComponent: () =>
          import('./invoices/invoices-list.page').then((m) => m.InvoicesListPage),
      },
      {
        path: 'invoices/:id',
        title: 'Invoice · EBT Portal',
        loadComponent: () =>
          import('./invoices/invoice-detail.page').then((m) => m.InvoiceDetailPage),
      },
      {
        path: 'messages',
        title: 'Messages · EBT Portal',
        loadComponent: () => import('./messages/messages.page').then((m) => m.MessagesPage),
      },
      {
        path: 'profile',
        title: 'Profile · EBT Portal',
        loadComponent: () => import('./profile/profile.page').then((m) => m.ProfilePage),
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
